import { useState, useCallback, useMemo, useRef } from 'react'

import { useFetch } from '@core/hooks'

import { Box, Button, Text } from '@components/atoms'
import Icon from '@components/molecules/Icon/Icon'
import {
  StyledMultipleFileUploadField,
  FieldLabel,
  ErrorMessage,
  HelperMessage
} from './MultipleFileUploadField.styled'
import { useTranslation } from 'react-i18next'
import { noop, some, get } from 'lodash'
import { getFileExtension, callToast, removeFileExtension } from '@core/utils'
import { UPLOAD_POLICIES } from '@constants/files'
import { APIS } from '@constants/apis'
import { downloadFileFromUrl, downloadURI } from '@core/utils/download'

const keepFileName = (fileName) => fileName
const MAX_FILE_COUNT = 3

const getUniqueFileNames = (value) => {
  if (typeof value !== 'string') return []

  return [
    ...new Set(value.split(',').map((fileName) => fileName.trim()))
  ].filter(Boolean)
}

const MultipleFileUploadField = ({
  name,
  label,
  error,
  helperMessage,
  value,
  touched,
  className,
  acceptList = [],
  uploadKey = 'key',
  policies = UPLOAD_POLICIES.FILE,
  setFieldValue = noop,
  required = false,
  onUploadFiles = noop,
  onDownloadExistingFile,
  transformFile = async (file) => file,
  normalizeFileName = keepFileName
}) => {
  const { t } = useTranslation()
  const [isLoading, setIsLoading] = useState(false)
  const [fileList, setFileList] = useState([])
  const currentFileNames = useMemo(() => getUniqueFileNames(value), [value])
  const isUploadDisabled = currentFileNames.length >= MAX_FILE_COUNT

  // const { execute: getPresignedUploadUrl } = useFetch(
  //   APIS.FILES.GET_PRESIGNED_UPLOAD_URL
  // )

  const { execute: _getPresignedDownloadUrl } = useFetch(
    APIS.FILES.GET_PRESIGNED_DOWNLOAD_URL
  )

  const getDownloadPresign = useCallback(
    (payload) => {
      _getPresignedDownloadUrl(
        { payload },
        {
          onSuccess: (res) => {
            const { url, key } = get(res, 'data')
            if (url && key) {
              downloadFileFromUrl(url, key)
              callToast({
                type: 'success',
                message: t('common.toast.get.success')
              })
            }
          },
          onFailed: () => {
            callToast({
              type: 'error',
              message: t('common.toast.get.failed')
            })
          }
        }
      )
    },
    [_getPresignedDownloadUrl, t]
  )

  const errorMessageWidget = useMemo(() => {
    return (
      error &&
      touched && (
        <ErrorMessage className={`error-text ${error ? 'input-error' : ''}`}>
          {error}
        </ErrorMessage>
      )
    )
  }, [error, touched])

  const helperMessageWidget = useMemo(() => {
    return (
      helperMessage && (
        <HelperMessage className="helper-text">{helperMessage}</HelperMessage>
      )
    )
  }, [helperMessage])

  const inputRef = useRef(null)

  const downloadRef = useCallback(
    (targetValue) => {
      const uploadFileList = inputRef.current?.fileList || []
      const file = uploadFileList.find(
        ({ originFileObj }) => originFileObj?.name === targetValue
      )?.originFileObj // ref only came from when u upload by component
      if (file) {
        return downloadURI(file)
      }
      if (targetValue && onDownloadExistingFile) {
        return onDownloadExistingFile(targetValue)
      }
      if (targetValue) {
        const payload = {
          key: targetValue,
          fileExtension: '.' + getFileExtension({ name: targetValue }),
          [uploadKey]: removeFileExtension(targetValue)
        }
        return getDownloadPresign(payload)
      }
    },
    [getDownloadPresign, onDownloadExistingFile, uploadKey]
  )

  const displayValueWidget = useMemo(() => {
    if (currentFileNames.length === 0) return null

    return (
      <div>
        {currentFileNames.map((item, index) => (
          <Box key={index} display="flex" alignItems="center" mb="4px">
            <a onClick={() => downloadRef(item)}>
              <Text mt="9px" mr="6px" color="#3727c7" fontSize={14}>
                {item}
              </Text>
            </a>

            <Icon
              icon="Bin"
              size="16"
              color="#F47373"
              style={{ marginTop: '9px' }}
              onClick={() => {
                const newValue = currentFileNames.filter((x) => x !== item)
                const nextFileList = fileList.filter(
                  (f) => f.originFileObj?.name !== item
                )
                setFileList(nextFileList)
                onUploadFiles(nextFileList)
                setFieldValue(
                  name,
                  newValue.length > 0 ? newValue.join(',') : null
                )
              }}
            />
          </Box>
        ))}
      </div>
    )
  }, [
    downloadRef,
    name,
    setFieldValue,
    currentFileNames,
    fileList,
    onUploadFiles
  ])

  const isAcceptedFile = useCallback(
    (file) => {
      const type = getFileExtension(file).toLowerCase()

      if (acceptList.length === 0) {
        return true
      }

      return some(acceptList, (values) => values.toLowerCase() === type)
    },
    [acceptList]
  )

  const typeBeforeUpload = useCallback(
    (file) => {
      if (!isAcceptedFile(file)) {
        callToast({
          type: 'error',
          message: t('error.default.1007')
        })

        return false
      }

      return false
    },
    [isAcceptedFile, t]
  )

  const onChange = useCallback(
    async (info) => {
      if (info.event) {
        return // ❌ ข้าม progress event
      }

      const usedFileNames = new Set(currentFileNames)
      const incomingFileNames = new Set(
        info.fileList
          .map(({ originFileObj }) => originFileObj)
          .filter(Boolean)
          .map(({ name: fileName }) => normalizeFileName(fileName))
          .filter((fileName) => !usedFileNames.has(fileName))
      )

      if (currentFileNames.length + incomingFileNames.size > MAX_FILE_COUNT) {
        return
      }

      setIsLoading(true)
      const nextFileList = []

      for (const uploadFile of info.fileList) {
        const originalFile = uploadFile.originFileObj

        if (!originalFile || !isAcceptedFile(originalFile)) {
          continue
        }

        const normalizedFileName = normalizeFileName(originalFile.name)
        const normalizedFile =
          normalizedFileName === originalFile.name
            ? originalFile
            : new File([originalFile], normalizedFileName, {
                type: originalFile.type,
                lastModified: originalFile.lastModified
              })
        let transformedFile = normalizedFile
        let compressionMeta

        try {
          const transformResult = await transformFile(normalizedFile)
          if (
            transformResult &&
            Object.prototype.hasOwnProperty.call(transformResult, 'file')
          ) {
            transformedFile = transformResult.file || normalizedFile
            compressionMeta = transformResult.meta
          } else {
            transformedFile = transformResult || normalizedFile
          }
        } catch {
          transformedFile = normalizedFile
        }

        if (transformedFile.size > policies.SIZE) {
          callToast({
            type: 'error',
            message: 'Exceed limit'
          })
          continue
        }

        nextFileList.push({
          ...uploadFile,
          name: transformedFile.name,
          size: transformedFile.size,
          type: transformedFile.type,
          originFileObj: transformedFile,
          compressionMeta
        })
      }

      const uniqueNextFileList = nextFileList.filter((x) => {
        const fileName = x.originFileObj.name
        if (usedFileNames.has(fileName)) {
          return false
        }
        usedFileNames.add(fileName)
        return true
      })
      const combinedFileList = uniqueNextFileList.concat(fileList)

      setFileList((prev) => uniqueNextFileList.concat(prev))
      onUploadFiles(combinedFileList)
      const keyName = [
        ...currentFileNames,
        ...uniqueNextFileList.map((x) => x.originFileObj.name)
      ].join(',')
      setFieldValue(name, keyName || null)
      setIsLoading(false)
      return true
    },
    [
      onUploadFiles,
      setFieldValue,
      name,
      setFileList,
      currentFileNames,
      fileList,
      isAcceptedFile,
      policies.SIZE,
      transformFile,
      normalizeFileName
    ]
  )

  return (
    <Box className={className} display="flex" flexDirection="column">
      {label && (
        <FieldLabel htmlFor={name}>
          <span>{label}</span>
          {required && <span className="star">*</span>}
        </FieldLabel>
      )}
      <Box display="flex" alignItems="left" style={{ flexDirection: 'column' }}>
        <StyledMultipleFileUploadField
          ref={inputRef}
          multiple={true}
          showUploadList={false}
          beforeUpload={typeBeforeUpload}
          onChange={onChange}
          maxCount={MAX_FILE_COUNT}
          fileList={fileList}
          accept={policies.MIME_TYPE}
          disabled={isUploadDisabled}
        >
          <Button disabled={isUploadDisabled}>
            {!isLoading && <Icon icon="Upload" color="#fff" />}
            Upload
          </Button>
        </StyledMultipleFileUploadField>
        {displayValueWidget}
      </Box>
      {errorMessageWidget}
      {helperMessageWidget}
    </Box>
  )
}

export default MultipleFileUploadField
